/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useBinnedRestoreAll.ts
 * FILE_ID: EMAIL-INBOX-CONTROLLER-RESTORE-ALL-001
 * PURPOSE: "Restore all" for the Binned folder on the shared inbox frame.
 * WHY: The old EmailFolderShell Binned page offered Restore all; the inbox
 *      frame only had per-row and bulk verbs. Carried over so moving Binned
 *      onto the inbox shell loses no action.
 * LANDMINE: Restores the rows currently LOADED (same contract as the old
 *      page, which also only knew the fetched list). Uses `emails`, not
 *      `dedupedEmails`, so hidden multi-mailbox copies are restored too and
 *      do not resurface alone on the next poll.
 */

import { useState } from 'react'

export interface BinnedRestoreAllSource {
  folder: string
  emails: ReadonlyArray<{ id: string }>
  bulkAction: (ids: string[], action: string) => Promise<unknown>
  refetch: () => unknown
  refetchStats: () => unknown
  toast: (message: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

export function useBinnedRestoreAll(vm: BinnedRestoreAllSource) {
  const [restoringAll, setRestoringAll] = useState(false)
  const enabled = vm.folder === 'binned'
  const count = vm.emails.length

  const restoreAll = async () => {
    if (!enabled || restoringAll || count === 0) return
    const ids = vm.emails.map((email) => email.id)
    setRestoringAll(true)
    try {
      await vm.bulkAction(ids, 'restore')
      void vm.refetchStats()
      await vm.refetch()
      vm.toast(
        `Restored ${ids.length} email${ids.length === 1 ? '' : 's'} to the inbox.`,
        'success',
      )
    } catch {
      vm.toast('Couldn\u2019t restore all - try again.', 'error')
    } finally {
      setRestoringAll(false)
    }
  }

  return { enabled, restoringAll, count, restoreAll }
}
